import { SimpleDocCommand } from '../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../types/commands';
import { StandardToolResult } from '../../types/standardToolResults';
import fetchService from '../../services/fetchService';
import { API_BASE_URL } from '../../api/config';

export const google_ads_mutate: SimpleDocCommand = {
  id: 'google_ads_mutate',
  logicalName: 'google_ads_mutate',
  version: 'v1',
  description: `Create, update, or pause Google Ads campaigns, ad groups, and budgets.

**INTEGRATION REQUIRED:** User must connect Google Ads in Settings → Integrations first.
**CAUTION:** Mutate operations affect real ad spend. Always confirm with the user before executing.

**OPERATION TYPES:**
- campaign — create/update/pause/enable campaigns
- adGroup — create/update/pause/enable ad groups
- campaignBudget — create/update campaign budgets
- ad — create/update ads (adGroupAds)

**OPERATION FORMAT:**
Each operation is either "create", "update" (with "updateMask"), or "remove".

**EXAMPLES:**

Pause a campaign:
{
  "operationType": "campaign",
  "operations": [{
    "update": { "resourceName": "customers/1234567890/campaigns/111222333", "status": "PAUSED" },
    "updateMask": { "paths": ["status"] }
  }]
}

Create a campaign budget:
{
  "operationType": "campaignBudget",
  "operations": [{
    "create": {
      "name": "My Budget",
      "amountMicros": "5000000",
      "deliveryMethod": "STANDARD"
    }
  }]
}

Create a campaign:
{
  "operationType": "campaign",
  "operations": [{
    "create": {
      "name": "New Campaign",
      "status": "PAUSED",
      "advertisingChannelType": "SEARCH",
      "campaignBudget": "customers/1234567890/campaignBudgets/TEMP_BUDGET_ID",
      "targetSpend": {}
    }
  }]
}

**RESOURCE NAMES:** Use google_ads_query to look up resource names before mutating.
**MICROS:** amountMicros uses micros — $5.00 = 5000000.
`,

  schema: {
    parameters: [
      {
        name: 'operationType',
        type: 'string',
        description: 'Type of resource to mutate: campaign | adGroup | campaignBudget | ad',
        required: true,
        validator: (v: string) => {
          const valid = ['campaign', 'adGroup', 'campaignBudget', 'ad'];
          return valid.includes(v) ? null : `Must be one of: ${valid.join(', ')}`;
        }
      },
      {
        name: 'operations',
        type: 'array',
        description: 'Array of mutate operations. Each is an object with "create", "update", or "remove" key.',
        required: true,
      },
      {
        name: 'customerId',
        type: 'string',
        description: 'Optional: Google Ads customer ID (overrides default)',
        required: false,
      }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { operationType, operations, customerId } = params;

    try {
      const data = await fetchService(`${API_BASE_URL}/integrations/google-ads/mutate`, {
        method: 'POST',
        body: JSON.stringify({ operationType, operations, customerId }),
      });

      if (data.error || data.detail) {
        return {
          status: 'error',
          message: data.detail || 'Mutate operation failed',
          error: { code: 'mutate_failed', category: 'execution', details: data.detail },
          format: 'plaintext',
        };
      }

      const results = data.results || data.mutateOperationResponses || [];
      const partialErrors = data.partialFailureError;

      return {
        status: 'success',
        message: `Mutate completed. ${results.length} resource(s) affected.${partialErrors ? ' Note: some operations failed (partial failure).' : ''}`,
        data: { results, partialFailureError: partialErrors },
        format: 'json',
      };
    } catch (error: any) {
      const detail = error.data?.detail || error.message || 'Unknown error';
      return {
        status: 'error',
        message: detail.includes('not connected')
          ? 'Google Ads is not connected. Connect it in Settings → Integrations.'
          : `Google Ads mutate failed: ${detail}`,
        error: { code: 'mutate_error', category: error.status === 400 ? 'validation' : 'execution', details: detail },
        format: 'plaintext',
      };
    }
  }
};
